import React, { useEffect, useMemo, useRef, useState } from "react";
import { httpGet, httpPost } from "../../components/helpers/httpMethods";
import usePayStack from "../../hooks/usePayStack";
import { BillsReward } from "./BillsReward";
import "./bills.css";

// Your account → Pay bills (airtime, data, electricity, TV). Server: /api/bills/*. Paid with the same Paystack checkout as papers;
// the server confirms with Paystack and VTpass delivers. Shown in the menu only when bills are switched on for this reader.
const naira = (v) => `₦${Math.round(Number(v) || 0).toLocaleString("en-NG")}`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const errText = (r, fallback) => (r && r.er && (r.er.message || (typeof r.er === "string" ? r.er : ""))) || fallback;
const day = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const ICONS = {
  airtime: "M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM11 18h2",
  data: "M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M2 9a15 15 0 0 1 20 0M12 20h.01",
  electricity: "M13 2 3 14h9l-1 8 10-12h-9l1-8z",
  tv: "M3 7h18v12H3zM8 3l4 4 4-4",
};
const CHIPS = { airtime: [500, 1000, 2000, 5000], electricity: [5000, 10000, 20000, 50000] };
const DONE = { airtime: "Airtime sent", data: "Data sent", electricity: "Electricity paid", tv: "TV paid" };
const STATUS = {
  delivered: ["Delivered", "ok"], vending: ["Delivering", "info"], review: ["Being checked", "warn"],
  refund_due: ["Refund on its way", "warn"], refunding: ["Refund on its way", "warn"], refunded: ["Refunded", "info"],
};

// Network from the number's first digits. Moved numbers keep their old prefix, so the reader can change it.
const PREFIX = {
  mtn: ["0703", "0704", "0706", "0803", "0806", "0810", "0813", "0814", "0816", "0903", "0906", "0913", "0916", "07025", "07026"],
  airtel: ["0701", "0708", "0802", "0808", "0812", "0901", "0902", "0904", "0907", "0911", "0912"],
  glo: ["0705", "0805", "0807", "0811", "0815", "0905", "0915"],
  etisalat: ["0809", "0817", "0818", "0908", "0909"],
};
const digits = (v) => String(v || "").replace(/\D/g, "");
const localPhone = (v) => { const d = digits(v); return d.startsWith("234") && d.length === 13 ? `0${d.slice(3)}` : d; };
const networkOf = (v) => {
  const d = localPhone(v);
  if (d.length < 4) return null;
  return Object.keys(PREFIX).find((k) => PREFIX[k].some((p) => d.startsWith(p))) || null;
};
const serviceFor = (category, net) => (category === "data" ? `${net}-data` : net);

function Icon({ d, size = 22 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
}
const Tick = () => <Icon d="M20 6 9 17l-5-5" size={18} />;

export function Bills({ history }) {
  const [cat, setCat] = useState(null); // catalogue: { enabled, categories, limits }
  const [mine, setMine] = useState(null);
  const [tab, setTab] = useState("airtime");
  const [phone, setPhone] = useState("");
  const [net, setNet] = useState(null);
  const [showNets, setShowNets] = useState(false);
  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState("");
  const [plans, setPlans] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planFilter, setPlanFilter] = useState("");
  const [provider, setProvider] = useState("");
  const [meterType, setMeterType] = useState("prepaid");
  const [number, setNumber] = useState("");
  const [found, setFound] = useState(null);
  const [checking, setChecking] = useState(false);
  const [tvChoice, setTvChoice] = useState("renew");
  const [contact, setContact] = useState("");
  const [needContact, setNeedContact] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null); // checkout reply while reviewing
  const [stage, setStage] = useState("form"); // form | review | confirming | done
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [openToken, setOpenToken] = useState(null);
  const { setPayStackConfig, PayWithPayStack } = usePayStack();
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const loadMine = () => httpGet("bills/mine").then((r) => { if (alive.current) setMine(r && r.data ? r.data : { bills: [], again: [] }); });
  useEffect(() => {
    httpGet("bills/catalogue").then((r) => setCat(r && r.data ? r.data : { enabled: false, categories: [] }));
    loadMine();
  }, []);
  // Switched off by TheNewspaperStand (admin → Bills & profit): no trace of it, back to the account overview.
  useEffect(() => { if (cat && !cat.enabled && history) history.replace("/account"); }, [cat, history]);

  const category = (cat && cat.categories.find((c) => c.key === tab)) || null;
  const fee = category ? Number(category.fee) || 0 : 0;
  const isPhone = tab === "airtime" || tab === "data";
  const guessed = networkOf(phone);
  const network = net || guessed;
  const netName = (k) => ({ mtn: "MTN", airtel: "Airtel", glo: "Glo", etisalat: "9mobile" }[k] || "");

  // Data and TV plans come from VTpass (prices included).
  const planService = tab === "data" ? (network ? serviceFor("data", network) : null) : tab === "tv" ? provider : null;
  useEffect(() => {
    setPlans(null); setPlan(null);
    if (!planService || !cat || !cat.enabled) return;
    let on = true;
    httpGet(`bills/plans?serviceID=${encodeURIComponent(planService)}`).then((r) => {
      if (!on) return;
      setPlans(r && r.data && Array.isArray(r.data.plans) ? r.data.plans.filter((p) => p.amount > 0).sort((a, b) => a.amount - b.amount) : []);
    });
    return () => { on = false; };
  }, [planService, cat && cat.enabled]);

  const pickTab = (k) => {
    setTab(k); setError(""); setFound(null); setNumber(""); setCustom(""); setPlanFilter(""); setNeedContact(false);
    setAmount(k === "airtime" ? 1000 : k === "electricity" ? 10000 : 0);
    const first = cat && cat.categories.find((c) => c.key === k);
    setProvider(k === "electricity" || k === "tv" ? (first && first.providers[0] ? first.providers[0].serviceID : "") : "");
  };
  useEffect(() => { if (cat && cat.enabled && !provider) pickTab(tab); }, [cat]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = custom ? Math.round(Number(digits(custom))) : amount;
  const price = tab === "data" ? (plan ? plan.amount : 0)
    : tab === "tv" ? (tvChoice === "renew" && found && found.renewalAmount && provider !== "startimes" ? found.renewalAmount : plan ? plan.amount : 0)
      : value;
  const total = price ? price + fee : 0;
  const limits = (cat && cat.limits && cat.limits[tab]) || null;

  const check = async () => {
    setError(""); setFound(null); setChecking(true);
    const r = await httpPost("bills/verify", { serviceID: provider, number, meterType: tab === "electricity" ? meterType : undefined });
    setChecking(false);
    if (r && r.data && r.data.name) {
      setFound(r.data);
      if (tab === "tv") setTvChoice(r.data.renewalAmount && provider !== "startimes" ? "renew" : "plan");
    } else setError(errText(r, "We couldn’t check that number. Please try again."));
  };

  const ready = (() => {
    if (!category || !total) return false;
    if (isPhone) return Boolean(network) && localPhone(phone).length === 11 && (tab === "airtime" ? limits && value >= limits[0] && value <= limits[1] : Boolean(plan));
    if (tab === "electricity") return Boolean(found) && limits && value >= limits[0] && value <= limits[1];
    if (tab === "tv") return Boolean(found) && (tvChoice === "renew" || Boolean(plan));
    return false;
  })();

  // Step 1: the server prices the bill and opens a Paystack reference. Step 2 (review) opens Paystack.
  const startCheckout = async (body) => {
    setError(""); setBusy(true);
    const r = await httpPost("bills/checkout", body);
    setBusy(false);
    if (r && r.data && r.data.reference) {
      setPayStackConfig({ amount: r.data.amount, reference: r.data.reference, email: r.data.email });
      setOrder(r.data); setStage("review");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const msg = errText(r, "Something went wrong. Please try again.");
    if (/phone number so the provider/i.test(msg)) setNeedContact(true);
    setError(msg);
  };
  const submit = (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    const body = { serviceID: isPhone ? serviceFor(tab, network) : provider, number: isPhone ? phone : number };
    if (tab === "airtime" || tab === "electricity") body.amount = value;
    if (tab === "data" || (tab === "tv" && tvChoice === "plan")) body.variationCode = plan && plan.code;
    if (tab === "electricity") body.meterType = meterType;
    if (contact) body.phone = contact;
    startCheckout(body);
  };
  const payAgain = (a) => startCheckout({ repeatOf: a.id });

  // After Paystack says paid, ask the server until the bill is delivered (or clearly will be refunded).
  const confirm = async (reference) => {
    setStage("confirming");
    for (let i = 0; i < 24; i += 1) {
      const r = await httpGet(`bills/verify_payment/${encodeURIComponent(reference)}`);
      if (!alive.current) return;
      const d = r && r.data;
      if (r && r.er && !d) { setResult({ status: "payment_failed", message: errText(r, "The payment was not completed.") }); setStage("done"); return; }
      if (d && ["delivered", "refund_due", "refunding", "refunded", "review"].includes(d.status)) { setResult(d); setStage("done"); loadMine(); return; }
      await sleep(i < 8 ? 2500 : 5000);
    }
    setResult({ status: "slow", reference }); setStage("done"); loadMine();
  };
  const pay = () => {
    const ref = order.reference;
    PayWithPayStack(() => confirm(ref), () => {});
  };
  const reset = () => { setStage("form"); setOrder(null); setResult(null); setCopied(false); setFound(null); setNumber(""); setPlan(null); setCustom(""); };
  const copy = (t) => {
    const done = () => { setCopied(true); setTimeout(() => alive.current && setCopied(false), 2500); };
    if (navigator.clipboard) navigator.clipboard.writeText(t).then(done, () => {}); else done();
  };

  const visiblePlans = useMemo(() => {
    const q = planFilter.trim().toLowerCase();
    return (plans || []).filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [plans, planFilter]);

  const title = <div className="ac-title"><h1>Pay bills</h1><p>Airtime, data, electricity and TV. Delivered in seconds.</p></div>;
  if (!cat) return <>{title}<p className="ac-muted">Loading…</p></>;
  if (!cat.enabled) {
    return null; // switched off: the effect above sends the reader back to Your account
  }

  // ---- Review before paying ----
  if (stage === "review" && order) {
    const b = order.bill;
    return (
      <>
        {title}
        <section className="ac-card bl-card" aria-labelledby="bl-review">
          <h2 id="bl-review" className="bl-h2">Check before you pay</h2>
          <dl className="bl-sum">
            <div><dt>Bill</dt><dd>{b.provider} {b.category === "tv" ? "TV" : b.category}</dd></div>
            {b.plan ? <div><dt>Plan</dt><dd>{b.plan}</dd></div> : null}
            <div><dt>{b.category === "electricity" ? "Meter" : b.category === "tv" ? "Smartcard" : "Phone"}</dt><dd>{b.recipient}</dd></div>
            {b.customerName ? <div><dt>Name</dt><dd>{b.customerName}</dd></div> : null}
            <div><dt>Amount</dt><dd>{naira(b.amount)}</dd></div>
            <div><dt>Service fee</dt><dd>{b.fee ? naira(b.fee) : "Free"}</dd></div>
            <div className="bl-total"><dt>Total</dt><dd>{naira(b.total)}</dd></div>
          </dl>
          <button type="button" className="bl-pay" onClick={pay}>Pay {naira(b.total)}</button>
          <button type="button" className="bl-ghost" onClick={() => { setStage("form"); setOrder(null); }}>Change something</button>
          <p className="bl-note">Paid securely with Paystack · Money back automatically if it fails</p>
        </section>
      </>
    );
  }

  // ---- Confirming / done ----
  if (stage === "confirming") {
    return <>{title}<section className="ac-card bl-card bl-center" aria-live="polite"><span className="bl-spin" aria-hidden="true" /><h2 className="bl-h2">Delivering your bill…</h2><p className="ac-muted">This usually takes a few seconds. Please keep this page open.</p></section></>;
  }
  if (stage === "done" && result) {
    const r = result;
    let body;
    if (r.status === "delivered") {
      const line = r.category === "electricity" ? `${naira(r.amount)} for meter ${r.recipient}. Type the token into your meter.`
        : r.category === "tv" ? `${r.provider} ${r.plan ? `(${r.plan}) ` : ""}is paid. It can take up to 15 minutes to show on your decoder.`
          : `${r.plan ? r.plan : naira(r.amount)} ${r.provider} ${r.category} is on ${r.recipient}.`;
      body = (
        <>
          <span className="bl-ok"><Icon d="M20 6 9 17l-5-5" size={30} /></span>
          <h2 className="bl-h2 bl-big">{DONE[r.category] || "Bill paid"}</h2>
          <p className="ac-muted">{line}</p>
          {r.token ? (
            <div className="bl-token">
              <span>Your token</span>
              <strong>{r.token}</strong>
              {r.units ? <small>{r.units}</small> : null}
              <button type="button" className="bl-ghost" onClick={() => copy(r.token)}>{copied ? "Copied" : "Copy token"}</button>
            </div>
          ) : null}
          <p className="bl-note">Receipt saved in Payments and sent to your email.</p>
        </>
      );
    } else if (["refund_due", "refunding", "refunded"].includes(r.status)) {
      body = <><h2 className="bl-h2 bl-big">We couldn’t deliver this bill</h2><p className="ac-muted">Your money is coming back to the way you paid, fee included. We’ll email you when the refund is made. Banks can take a few working days to show it.</p></>;
    } else if (r.status === "review") {
      body = <><h2 className="bl-h2 bl-big">We’re checking this one</h2><p className="ac-muted">Your payment arrived and our team is checking it with the provider. We’ll email you. You won’t be charged twice.</p>{r.reference ? <p className="bl-note">Reference: {r.reference}</p> : null}</>;
    } else if (r.status === "slow") {
      body = <><h2 className="bl-h2 bl-big">We’ll finish this shortly</h2><p className="ac-muted">Paystack has your payment and we’re still delivering it. It will show under Recent bills, and we’ll email your receipt. If it can’t be delivered, you get your money back automatically.</p><p className="bl-note">Reference: {r.reference}</p></>;
    } else {
      body = <><h2 className="bl-h2 bl-big">Payment not completed</h2><p className="ac-muted">{r.message || "Nothing was charged."}</p></>;
    }
    return <>{title}<section className="ac-card bl-card bl-center" aria-live="polite">{body}<button type="button" className="bl-ghost" onClick={reset}>Pay another bill</button></section></>;
  }

  // ---- Form ----
  const again = (mine && mine.again) || [];
  const bills = (mine && mine.bills) || [];
  return (
    <>
      {title}
      {again.length ? (
        <div className="bl-again">
          <span>Pay again:</span>
          {again.map((a) => (
            <button type="button" key={a.id} disabled={busy} onClick={() => payAgain(a)}>{a.provider} {a.recipient} · {a.plan || naira(a.amount)}</button>
          ))}
        </div>
      ) : null}

      <BillsReward history={history} />

      <form className="ac-card bl-card" onSubmit={submit} aria-label="Pay a bill">
        <div className="bl-tabs" role="tablist" aria-label="Bill type">
          {cat.categories.map((c) => (
            <button type="button" key={c.key} role="tab" aria-selected={tab === c.key} className={tab === c.key ? "on" : ""} onClick={() => pickTab(c.key)}>
              <Icon d={ICONS[c.key]} /><span>{c.label}</span>
            </button>
          ))}
        </div>

        {isPhone ? (
          <>
            <label className="bl-field">Phone number
              <input type="tel" inputMode="tel" autoComplete="tel" placeholder="0803 123 4567" value={phone} onChange={(e) => { setPhone(e.target.value); setNet(null); }} />
            </label>
            {network ? (
              <div className="bl-net"><Tick /><span>{netName(network)} line</span><button type="button" onClick={() => setShowNets(!showNets)}>Change</button></div>
            ) : localPhone(phone).length >= 4 ? <div className="bl-net"><span>Choose the network</span></div> : null}
            {showNets || (!network && localPhone(phone).length >= 4) ? (
              <div className="bl-grid4">
                {["mtn", "airtel", "glo", "etisalat"].map((k) => (
                  <button type="button" key={k} aria-pressed={network === k} className={network === k ? "on" : ""} onClick={() => { setNet(k); setShowNets(false); }}>{netName(k)}</button>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <label className="bl-field">{tab === "electricity" ? "Electricity company" : "TV provider"}
              <select value={provider} onChange={(e) => { setProvider(e.target.value); setFound(null); }}>
                {category && category.providers.map((p) => <option key={p.serviceID} value={p.serviceID}>{p.name}</option>)}
              </select>
            </label>
            {tab === "electricity" ? (
              <div className="bl-grid2" role="group" aria-label="Meter type">
                {[["prepaid", "Prepaid"], ["postpaid", "Postpaid"]].map(([k, l]) => (
                  <button type="button" key={k} aria-pressed={meterType === k} className={meterType === k ? "on" : ""} onClick={() => { setMeterType(k); setFound(null); }}>{l}</button>
                ))}
              </div>
            ) : null}
            <div className="bl-row">
              <label className="bl-field">{tab === "electricity" ? "Meter number" : "Smartcard / IUC number"}
                <input type="text" inputMode="numeric" value={number} onChange={(e) => { setNumber(e.target.value); setFound(null); }} />
              </label>
              <button type="button" className="bl-check" disabled={checking || digits(number).length < 6} onClick={check}>{checking ? "Checking…" : tab === "electricity" ? "Check meter" : "Check card"}</button>
            </div>
            {found ? (
              <div className="bl-found"><Tick /><span><strong>{found.name}</strong>{found.address ? <><br />{found.address}</> : null}{found.currentPackage ? <><br />Current package: {found.currentPackage}</> : null}</span></div>
            ) : null}
          </>
        )}

        {tab === "airtime" || (tab === "electricity" && found) ? (
          <div className="bl-field">
            <span>Amount</span>
            <div className="bl-grid4">
              {CHIPS[tab].map((a) => (
                <button type="button" key={a} className={!custom && amount === a ? "on" : ""} aria-pressed={!custom && amount === a} onClick={() => { setAmount(a); setCustom(""); }}>{naira(a)}</button>
              ))}
            </div>
            <input type="text" inputMode="numeric" aria-label="Another amount" placeholder={limits ? `Another amount (${naira(limits[0])} to ${naira(limits[1])})` : "Another amount"} value={custom} onChange={(e) => setCustom(digits(e.target.value))} />
          </div>
        ) : null}

        {tab === "tv" && found ? (
          <div className="bl-field">
            <span>What to pay</span>
            {found.renewalAmount && provider !== "startimes" ? (
              <button type="button" className={`bl-plan ${tvChoice === "renew" ? "on" : ""}`} onClick={() => setTvChoice("renew")}><span>Renew {found.currentPackage || "current package"}</span><strong>{naira(found.renewalAmount)}</strong></button>
            ) : null}
            <button type="button" className={`bl-plan ${tvChoice === "plan" ? "on" : ""}`} onClick={() => setTvChoice("plan")}><span>Choose a package</span></button>
          </div>
        ) : null}

        {(tab === "data" && network) || (tab === "tv" && found && tvChoice === "plan") ? (
          <div className="bl-field">
            <span>{tab === "data" ? "Plan" : "Package"}</span>
            {plans === null ? <p className="ac-muted">Loading plans…</p> : !plans.length ? <p className="ac-muted">Plans couldn’t be loaded. Please try again shortly.</p> : (
              <>
                {plans.length > 8 ? <input type="search" aria-label="Find a plan" placeholder="Find a plan, e.g. 1GB or monthly" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} /> : null}
                <div className="bl-plans">
                  {visiblePlans.map((p) => (
                    <button type="button" key={p.code} className={`bl-plan ${plan && plan.code === p.code ? "on" : ""}`} onClick={() => setPlan(p)}><span>{p.name}</span><strong>{naira(p.amount)}</strong></button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}

        {needContact && !isPhone ? (
          <label className="bl-field">Your phone number (for the provider’s SMS copy)
            <input type="tel" inputMode="tel" value={contact} onChange={(e) => setContact(e.target.value)} />
          </label>
        ) : null}

        {error ? <p className="bl-error" role="alert">{error}</p> : null}
        <button type="submit" className="bl-pay" disabled={!ready || busy}>{busy ? "Getting ready…" : total ? `Continue · ${naira(total)}` : "Continue"}</button>
        <p className="bl-note">{fee ? `Includes ${naira(fee)} service fee` : "No fee"} · Paid securely with Paystack · Money back automatically if it fails</p>
      </form>

      {bills.length ? (
        <section className="ac-card">
          <div className="ac-head"><h2>Recent bills</h2></div>
          <ul className="bl-list">
            {bills.slice(0, 12).map((b) => {
              const st = STATUS[b.status] || ["Not completed", ""];
              return (
                <li key={b.reference}>
                  <span className="bl-li-icon"><Icon d={ICONS[b.category]} size={18} /></span>
                  <span className="bl-li-main">
                    <strong>{b.provider} {b.category === "tv" ? "TV" : b.category}{b.plan ? ` · ${b.plan}` : ""}</strong>
                    <small>{b.recipient} · {day(b.createdAt)} · {naira(b.total)}</small>
                    {b.token && openToken === b.reference ? <code className="bl-li-token">{b.token}{b.units ? ` · ${b.units}` : ""}</code> : null}
                  </span>
                  {b.token ? <button type="button" className="ac-link" onClick={() => setOpenToken(openToken === b.reference ? null : b.reference)}>{openToken === b.reference ? "Hide token" : "Show token"}</button> : null}
                  <span className={`ac-pill ${st[1]}`}>{st[0]}</span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </>
  );
}
